// MACHINERY V2 (Stroje) — value formatters shared by the profile tabs (M2.5).
//
// Pure functions only. Nothing here reads Redux, i18n or the DOM, so each one
// is directly testable and none of them can drift between two tabs.
//
// ── FIX 2 (ticket §7): `file.size === null` renders "NaN undefined" today.
//    shared/models/models.ts:413-420 is
//        if (size === 0) return "0 B";
//        const index = Math.floor(Math.log(size) / Math.log(1024));
//        return (size / Math.pow(1024, index)).toFixed(precision) + " " + units[index];
//    With `size = null`: Math.log(null) is Math.log(0) = -Infinity, so `index`
//    is -Infinity, `Math.pow(1024, -Infinity)` is 0, `null / 0` is NaN,
//    NaN.toFixed(1) is the STRING "NaN", and `units[-Infinity]` is undefined —
//    giving the literal "NaN undefined". `undefined` takes the same path via
//    Math.log(undefined) = NaN. The DTO declares `Size` as `long?`
//    (data package §6), so null is a normal value, not a corruption.
//
//    The shared helper is NOT edited: shared/models/models.ts is 411 lines,
//    already over the 300-line cap and shared with ~8 live surfaces, and the
//    v1 Manuals tab must stay byte-unchanged. This is the v2 formatter; it
//    behaves identically for every value the shared one handles correctly.
export const DOC_SIZE_UNITS = ["B", "KB", "MB"] as const;

/**
 * Human file size, or "—" when there is no size to show.
 *
 * `0` is a real, meaningful value ("0 B") and stays distinct from "no size" —
 * that distinction is the whole point of the fix, so do not collapse the two
 * with a falsy test.
 */
export const formatDocSize = (size?: number | null): string => {
  if (size === null || size === undefined) return "—";
  if (typeof size !== "number" || Number.isNaN(size) || size < 0) return "—";
  if (size === 0) return "0 B";
  // Clamped so a file larger than the unit table cannot yield `undefined` the
  // way the shared helper would (unreachable at the 11 MB upload cap, but the
  // list also renders sizes that predate any cap).
  const index = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    DOC_SIZE_UNITS.length - 1
  );
  return (size / Math.pow(1024, index)).toFixed(1) + " " + DOC_SIZE_UNITS[index];
};

/** Uppercase extension for the file tile, or null when there is no file. */
export const fileExtension = (name?: string | null): string | null => {
  if (!name) return null;
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return null;
  return name.slice(dot + 1).toUpperCase().slice(0, 4);
};

const pad = (n: number): string => (n < 10 ? "0" + n : String(n));

/**
 * `DD.MM.YYYY, HH:mm` (or date-only). Both the v1 SR list and the v1 Manuals
 * list render their timestamps through react-moment with this literal format,
 * and the artboards show the same — so it is deliberately NOT locale-derived:
 * switching language must not reorder a maintenance timestamp.
 */
export const formatTabDate = (value?: Date | string | null, withTime = true): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const day = pad(d.getDate()) + "." + pad(d.getMonth() + 1) + "." + d.getFullYear();
  return withTime ? day + ", " + pad(d.getHours()) + ":" + pad(d.getMinutes()) : day;
};
